import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ChatMessage,
  DocumentAction,
  DocumentComment,
  ENVIRONMENT,
  HistoryStatus,
  LoanDetailResponse,
  LoanTeam,
  LoanTeamMember,
  OriginationDocument,
  OriginationTodos,
  RequestStatus,
  TodoItem,
  ToDoSummaryPerBorrower,
  UploadResponse,
} from '@ld.consumer-portal/models';
import { NativeDocumentService, LoggingService} from '@ld.consumer-portal/utils';
import { firstValueFrom } from 'rxjs';

interface OriginationTodosApiResponse {
  Requests?: TodoItemApiResponse[];
  requests?: TodoItemApiResponse[];
  RequestDocuments?: TodoItemApiResponse[];
  requestDocuments?: TodoItemApiResponse[];
  Urlas?: string[][];
  urlas?: string[][];
  TodoCounts?: TodoCountApiResponse[];
  todoCounts?: TodoCountApiResponse[];
  MelloNow?: { enabled: boolean; url?: string };
  melloNow?: { enabled: boolean; url?: string };
}

interface TodoCountApiResponse {
  BorrowerId?: string;
  borrowerId?: string;
  BorrowerType?: string;
  borrowerType?: string;
  ToDoCount?: number;
  toDoCount?: number;
}

interface TodoItemApiResponse {
  UniqId?: string;
  uniqId?: string;
  PriorityOrder?: number;
  priorityOrder?: number;
  RequestOrder?: number;
  requestOrder?: number;
  RequestId?: string;
  requestId?: string;
  RequestStatus?: RequestStatus;
  requestStatus?: RequestStatus;
  RequestedOn?: string;
  requestedOn?: string;
  BorrowerId?: string;
  borrowerId?: string;
  DocumentName?: string;
  documentName?: string;
  DocumentId?: string;
  documentId?: string;
  DocumentNotes?: string;
  documentNotes?: string;
  RequestedBy?: string;
  requestedBy?: string;
  ConditionGuid?: string;
  conditionGuid?: string;
  DocAction?: DocumentAction;
  docAction?: DocumentAction;
  AgentNotes?: string;
  agentNotes?: string;
  Flag?: string;
  flag?: string;
  Comments?: DocumentCommentApiResponse[];
  comments?: DocumentCommentApiResponse[];
  RejectDetails?: string;
  rejectDetails?: string;
  IsAdditionalDocument?: boolean;
  isAdditionalDocument?: boolean;
  ViewableBorrowerIds?: string[];
  viewableBorrowerIds?: string[];
  UploadNotes?: string;
  uploadNotes?: string;
}

interface DocumentCommentApiResponse {
  RequestGuid?: string;
  requestGuid?: string;
  Comment?: string;
  comment?: string;
  DateAdded?: string;
  dateAdded?: string;
  AddedBy?: string;
  addedBy?: string;
  // Frontend model field names (used by mock data)
  id?: string;
  text?: string;
  author?: string;
  createdAt?: string;
}

interface LoanContactsApiResponse {
  Contacts?: LoanContactApiResponse[];
  contacts?: LoanContactApiResponse[];
  Support?: SupportContactApiResponse[];
  support?: SupportContactApiResponse[];
  loanId?: string;
  primaryContact?: LoanTeamMember;
  members?: LoanTeamMember[];
  chatHistory?: ChatMessage[];
}

interface LoanContactApiResponse {
  AgentId?: string;
  agentId?: string;
  FirstName?: string;
  firstName?: string;
  LastName?: string;
  lastName?: string;
  Title?: string;
  title?: string;
  IsLoanTeam?: boolean;
  isLoanTeam?: boolean;
  Description?: string;
  description?: string;
  Nmls?: string;
  nmls?: string;
  Phones?: ContactPhoneApiResponse[];
  phones?: ContactPhoneApiResponse[];
  Email?: string;
  email?: string;
  ADGuid?: string;
  adGuid?: string;
}

interface ContactPhoneApiResponse {
  Label?: string;
  label?: string;
  Number?: string;
  number?: string;
}

interface SupportContactApiResponse {
  Name?: string;
  name?: string;
  Description?: string;
  description?: string;
  Phones?: ContactPhoneApiResponse[];
  phones?: ContactPhoneApiResponse[];
  Email?: string;
  email?: string;
}

interface HistoryDocumentApiResponse {
  historyId?: string;
  historyStatus?: string;
  borrowerId?: string;
  documentId?: string;
  documentName?: string;
  documentNotes?: string;
  conditionGuid?: string;
  fileExtension?: string;
  docTypeExtension?: string;
  filename?: string;
  requestedBy?: string;
  requestedOn?: string;
  updatedOn?: string;
  processedOn?: string;
  uploadNote?: string;
  uploadedOn?: string;
  sideUpdatedBy?: string;
  sideUpdatedOn?: string;
  docAction?: DocumentAction;
  actionBy?: string;
  agentNotes?: string;
  rejectDetails?: string;
  borrowerFirstName?: string;
  borrowerLastName?: string;
  conditionInfo?: {
    conditionGuid?: string;
    conditionName?: string;
    conditionDescription?: string;
    conditionStatus?: string;
  };
  adrInfo?: {
    adrStatusType?: string;
    componentSourceType?: string;
    documentName?: string;
    documentId?: string;
    adrStatusDate?: string;
    adrNote?: string;
  };
  requestGuid?: string;
  uniqId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OriginationService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly nativeDocumentService = inject(NativeDocumentService);
  private readonly loggingService = inject(LoggingService);

  private get cpApiUrl(): string {
    return this.env.appSettings.cpApiUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  private toEpsLoanId(loanId: string): string {
    if (loanId.startsWith('1_MTG_')) {
      return loanId;
    }
    return `1_MTG_${loanId}`;
  }

  private toRawDocumentId(documentId: string): string {
    if (documentId.startsWith('1_')) {
      return documentId.substring(2);
    }
    return documentId;
  }

  async getLoanDetails(loanId: string): Promise<LoanDetailResponse> {
    const epsLoanId = this.toEpsLoanId(loanId);
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${epsLoanId}`;
    return firstValueFrom(this.http.get<LoanDetailResponse>(url));
  }

  async getDocumentHistory(loanId: string): Promise<OriginationDocument[]> {
    const epsLoanId = this.toEpsLoanId(loanId);
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${epsLoanId}/documents`;
    const response = await firstValueFrom(this.http.get<{ documents: HistoryDocumentApiResponse[] }>(url));
    return (response.documents || []).map((doc, index) => this.mapHistoryDocument(doc, index));
  }

  private mapHistoryDocument(doc: HistoryDocumentApiResponse, index: number): OriginationDocument {
    const historyId = doc.historyId ?? `history-${index}`;
    const documentId = doc.documentId ?? '';

    return {
      historyId,
      historyStatus: this.mapHistoryStatus(doc.historyStatus),
      borrowerId: doc.borrowerId,
      documentId,
      documentName: doc.documentName ?? '',
      documentNotes: doc.documentNotes,
      conditionGuid: doc.conditionGuid,
      fileExtension: doc.fileExtension,
      docTypeExtension: doc.docTypeExtension,
      filename: doc.filename,
      requestedBy: doc.requestedBy,
      requestedOn: doc.requestedOn,
      updatedOn: doc.updatedOn,
      processedOn: doc.processedOn,
      uploadNote: doc.uploadNote,
      uploadedOn: doc.uploadedOn,
      sideUpdatedBy: doc.sideUpdatedBy,
      sideUpdatedOn: doc.sideUpdatedOn,
      docAction: doc.docAction,
      actionBy: doc.actionBy,
      agentNotes: doc.agentNotes,
      rejectDetails: doc.rejectDetails,
      borrowerFirstName: doc.borrowerFirstName,
      borrowerLastName: doc.borrowerLastName,
      conditionInfo: doc.conditionInfo,
      adrInfo: doc.adrInfo,
      requestGuid: doc.requestGuid,
      uniqId: doc.uniqId ?? `doc-${documentId}-${index}`,
      status: this.mapHistoryStatusToString(doc.historyStatus),
      uploadDate: doc.uploadedOn ?? doc.updatedOn,
      documentType: doc.docTypeExtension,
    };
  }

  private mapHistoryStatus(status?: string): HistoryStatus | undefined {
    if (!status) return undefined;

    const statusMap: Record<string, HistoryStatus> = {
      ESign: HistoryStatus.ESign,
      Decline: HistoryStatus.Decline,
      Expired: HistoryStatus.Expired,
      Confirm: HistoryStatus.Confirm,
      InkSign: HistoryStatus.InkSign,
      Submitted: HistoryStatus.Submitted,
      Requested: HistoryStatus.Requested,
      AdrGenerated: HistoryStatus.AdrGenerated,
      Accepted: HistoryStatus.Accepted,
      Rejected: HistoryStatus.Rejected,
      MarkedAs: HistoryStatus.MarkedAs,
      AgentUpload: HistoryStatus.AgentUpload,
      AgentMoveDocumentSide: HistoryStatus.AgentMoveDocumentSide,
      AgentUpdateAttribute: HistoryStatus.AgentUpdateAttribute,
      AgentAddComment: HistoryStatus.AgentAddComment,
      AgentSetBorrowerVisible: HistoryStatus.AgentSetBorrowerVisible,
    };

    return statusMap[status] ?? undefined;
  }

  private mapHistoryStatusToString(status?: string): string | undefined {
    if (!status) return undefined;

    const displayMap: Record<string, string> = {
      ESign: 'Signed',
      Decline: 'Declined',
      Expired: 'Expired',
      Confirm: 'Confirmed',
      InkSign: 'Ink Signed',
      Submitted: 'Submitted',
      Requested: 'Requested',
      AdrGenerated: 'Auto-Generated',
      Accepted: 'Accepted',
      Rejected: 'Rejected',
      MarkedAs: 'Completed',
      AgentUpload: 'Uploaded',
      AgentMoveDocumentSide: 'Moved',
      AgentUpdateAttribute: 'Updated',
      AgentAddComment: 'Comment Added',
      AgentSetBorrowerVisible: 'Made Visible',
    };

    return displayMap[status] ?? status;
  }

  async getDocumentHistoryGroups(loanId: string): Promise<unknown> {
    const epsLoanId = this.toEpsLoanId(loanId);
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${epsLoanId}/documents/history/groups`;
    const response = await firstValueFrom(this.http.get<unknown>(url));
    return response;
  }

  async getLoanTeam(loanId: string): Promise<LoanTeam> {
    const epsLoanId = this.toEpsLoanId(loanId);
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${epsLoanId}/contacts`;
    const response = await firstValueFrom(this.http.get<LoanContactsApiResponse>(url));
    return this.mapLoanContactsResponse(loanId, response);
  }

  async getTodos(loanId: string, includePending = true, includeCondition = true): Promise<OriginationTodos> {
    const epsLoanId = this.toEpsLoanId(loanId);
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${epsLoanId}/requests`;
    const response = await firstValueFrom(
      this.http.get<OriginationTodosApiResponse>(url, {
        params: {
          pending: includePending.toString(),
          condition: includeCondition.toString(),
        },
      }),
    );
    return this.mapTodosResponse(response);
  }

  private mapTodosResponse(response: OriginationTodosApiResponse): OriginationTodos {
    const requests =
      response.Requests ?? response.requests ?? response.RequestDocuments ?? response.requestDocuments ?? [];
    return {
      urlas: response.Urlas ?? response.urlas,
      requestDocuments: requests.map((doc) => this.mapTodoItem(doc)),
      todoCounts: (response.TodoCounts ?? response.todoCounts ?? []).map((tc) => this.mapTodoCount(tc)),
      melloNow: response.MelloNow ?? response.melloNow,
    };
  }

  private mapTodoCount(item: TodoCountApiResponse): ToDoSummaryPerBorrower {
    return {
      borrowerId: item.BorrowerId ?? item.borrowerId ?? '',
      borrowerType: (item.BorrowerType ?? item.borrowerType ?? 'primary') as 'primary' | 'coborrower',
      toDoCount: item.ToDoCount ?? item.toDoCount ?? 0,
    };
  }

  private mapTodoItem(doc: TodoItemApiResponse): TodoItem {
    const comments = this.mapComments(doc.Comments ?? doc.comments);
    return {
      uniqId: doc.UniqId ?? doc.uniqId ?? '',
      requestOrder: doc.PriorityOrder ?? doc.priorityOrder ?? doc.RequestOrder ?? doc.requestOrder ?? 0,
      requestId: doc.RequestId ?? doc.requestId ?? '',
      requestStatus: doc.RequestStatus ?? doc.requestStatus ?? RequestStatus.REQUESTED,
      requestedOn: doc.RequestedOn ?? doc.requestedOn ?? '',
      borrowerId: doc.BorrowerId ?? doc.borrowerId ?? '',
      documentName: doc.DocumentName ?? doc.documentName ?? '',
      documentId: doc.DocumentId ?? doc.documentId,
      documentNotes: doc.DocumentNotes ?? doc.documentNotes,
      requestedBy: doc.RequestedBy ?? doc.requestedBy,
      conditionGuid: doc.ConditionGuid ?? doc.conditionGuid,
      docAction: doc.DocAction ?? doc.docAction,
      agentNotes: doc.AgentNotes ?? doc.agentNotes,
      flag: doc.Flag ?? doc.flag,
      viewableBorrowerIds: doc.ViewableBorrowerIds ?? doc.viewableBorrowerIds,
      comments,
      expanded: !!comments?.length,
      rejectDetails: doc.RejectDetails ?? doc.rejectDetails,
      isAdditionalDocument: doc.IsAdditionalDocument ?? doc.isAdditionalDocument,
      uploadNotes: doc.UploadNotes ?? doc.uploadNotes,
    };
  }

  private mapComments(comments?: DocumentCommentApiResponse[]): DocumentComment[] | undefined {
    if (!comments?.length) return undefined;
    return comments.map((c) => ({
      id: c.id ?? c.RequestGuid ?? c.requestGuid ?? '',
      text: c.text ?? c.Comment ?? c.comment ?? '',
      author: c.author ?? c.AddedBy ?? c.addedBy ?? '',
      createdAt: c.createdAt ?? c.DateAdded ?? c.dateAdded ?? '',
    }));
  }

  private mapLoanContactsResponse(loanId: string, response: LoanContactsApiResponse): LoanTeam {
    if (response.primaryContact) {
      return {
        loanId: response.loanId ?? loanId,
        primaryContact: response.primaryContact,
        members: response.members ?? [],
        chatHistory: response.chatHistory ?? [],
      };
    }

    const contacts = response.Contacts ?? response.contacts ?? [];
    const members: LoanTeamMember[] = contacts.map((contact) => this.mapLoanContact(contact));

    const primaryContact = this.findPrimaryContact(members, contacts);

    return {
      loanId,
      primaryContact,
      members: members.filter((m) => m.id !== primaryContact.id),
      chatHistory: [],
    };
  }

  private mapLoanContact(contact: LoanContactApiResponse): LoanTeamMember {
    const firstName = contact.FirstName ?? contact.firstName ?? '';
    const lastName = contact.LastName ?? contact.lastName ?? '';
    const title = contact.Title ?? contact.title ?? '';
    const phones = contact.Phones ?? contact.phones ?? [];
    const primaryPhone = phones.length > 0 ? (phones[0].Number ?? phones[0].number ?? '') : '';

    const adGuid = contact.ADGuid ?? contact.adGuid;
    const isValidGuid = adGuid && adGuid !== '00000000-0000-0000-0000-000000000000';

    return {
      id: (isValidGuid ? adGuid : null) ?? contact.AgentId ?? contact.agentId ?? '',
      name: `${firstName} ${lastName}`.trim() || 'Loan Team Member',
      role: this.inferRoleFromTitle(title),
      roleDisplayName: title || 'Team Member',
      phone: primaryPhone,
      email: contact.Email ?? contact.email,
      nmlsId: contact.Nmls ?? contact.nmls,
      adGuid: isValidGuid ? adGuid : undefined,
    };
  }

  private findPrimaryContact(members: LoanTeamMember[], apiContacts: LoanContactApiResponse[]): LoanTeamMember {
    const loanTeamIndex = apiContacts.findIndex((c) => c.IsLoanTeam ?? c.isLoanTeam);
    if (loanTeamIndex >= 0 && members[loanTeamIndex]) {
      return members[loanTeamIndex];
    }

    const loanOfficer = members.find((m) => m.role === 'loan-officer');
    if (loanOfficer) {
      return loanOfficer;
    }

    return (
      members[0] ?? {
        id: 'default',
        name: 'Your Loan Team',
        role: 'loan-officer',
        roleDisplayName: 'Loan Officer',
        phone: '',
        email: '',
      }
    );
  }

  private inferRoleFromTitle(
    title: string,
  ): 'loan-officer' | 'production-assistant' | 'processor' | 'underwriter' | 'closer' | 'senior-account-manager' | 'processing-manager' {
    const lowerTitle = title.toLowerCase();
    if (
      lowerTitle.includes('loan officer') ||
      lowerTitle.includes('lending officer') ||
      lowerTitle.includes('banker')
    ) {
      return 'loan-officer';
    }
    if (lowerTitle.includes('senior account manager') || lowerTitle.includes('sr. account manager')) {
      return 'senior-account-manager';
    }
    if (lowerTitle.includes('processing manager')) {
      return 'processing-manager';
    }
    if (lowerTitle.includes('production assistant')) {
      return 'production-assistant';
    }
    if (lowerTitle.includes('processor')) {
      return 'processor';
    }
    if (lowerTitle.includes('underwriter')) {
      return 'underwriter';
    }
    if (lowerTitle.includes('closer') || lowerTitle.includes('closing')) {
      return 'closer';
    }
    return 'loan-officer';
  }

  async getTodoSummary(loanId: string): Promise<{ todos: Array<{ borrowerId: string; toDoCount: number }> }> {
    const epsLoanId = this.toEpsLoanId(loanId);
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${epsLoanId}/requests/summary`;
    return firstValueFrom(this.http.get<{ todos: Array<{ borrowerId: string; toDoCount: number }> }>(url));
  }

  async uploadDocument(loanId: string, requestId: string, file: File, notes?: string): Promise<UploadResponse> {
    const epsLoanId = this.toEpsLoanId(loanId);
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${epsLoanId}/requests/${requestId}`;

    const formData = new FormData();
    formData.append('file', file);
    if (notes) {
      formData.append('notes', notes);
    }

    return firstValueFrom(this.http.put<UploadResponse>(url, formData));
  }

  async uploadAdditionalDocument(loanId: string, file: File, notes?: string): Promise<UploadResponse> {
    const epsLoanId = this.toEpsLoanId(loanId);
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${epsLoanId}/requests`;

    const formData = new FormData();
    formData.append('file', file);
    if (notes) {
      formData.append('notes', notes);
    }

    return firstValueFrom(this.http.post<UploadResponse>(url, formData));
  }

  async triggerPostUpload(loanId: string): Promise<void> {
    const epsLoanId = this.toEpsLoanId(loanId);
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${epsLoanId}/postupload`;
    return firstValueFrom(this.http.patch<void>(url, {}));
  }

  async sendMessage(loanId: string, message: string): Promise<ChatMessage> {
    const epsLoanId = this.toEpsLoanId(loanId);
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${epsLoanId}/messages`;

    return firstValueFrom(this.http.post<ChatMessage>(url, { message }));
  }

  async getMessages(loanId: string): Promise<ChatMessage[]> {
    const epsLoanId = this.toEpsLoanId(loanId);
    const url = `${this.cpApiUrl}/${this.apiNamespace}/loans/${epsLoanId}/messages`;
    return firstValueFrom(this.http.get<ChatMessage[]>(url));
  }

  async downloadDocumentBlob(documentId: string): Promise<Blob> {
    const url = `${this.cpApiUrl}/${this.apiNamespace}/documents/${documentId}/download`;
    return firstValueFrom(this.http.get(url, { responseType: 'blob' }));
  }

  async downloadDocumentToBrowser(loanId: string, doc: OriginationDocument): Promise<void> {
    const filename = `${doc.documentName || doc.documentId}.pdf`;

    try {
      const blob = await this.downloadDocumentBlob(doc.documentId);
      await this.nativeDocumentService.saveDocument(blob, filename);
    } catch (err) {
      this.loggingService.logException(err, 'Failed to download document:');
    }
  }

  async viewDocumentInBrowser(loanId: string, doc: OriginationDocument): Promise<void> {
    const filename = `${doc.documentName || doc.documentId}.pdf`;

    try {
      const blob = await this.downloadDocumentBlob(doc.documentId);
      await this.nativeDocumentService.openDocument(blob, filename);
    } catch (err) {
      this.loggingService.logException(err, 'Failed to view document:');
    }
  }

  getDocumentCobrowseUrl(documentId: string): string {
    return `${this.cpApiUrl}/${this.apiNamespace}/documents/${documentId}/download`;
  }
}
